import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { getHotSettings } from './gridSettings'
import _ from 'lodash'
import { HotTable } from '@handsontable/react'
import Handsontable from 'handsontable'
import styles from './styles.scss'
import CardLoading from 'components/display/CardLoading'

class Spreadsheet extends Component {
  static propTypes = {    
    list: PropTypes.array.isRequired,            // data source for grid    
    formattingRanges: PropTypes.object,
    editMode: PropTypes.bool,
    filters: PropTypes.bool,
  }

  constructor(props) {
    super(props)
    this.state = {
      formattingRanges: {
        titleRanges: [],
        horizontalSubtitleRanges: [],
        verticalSubtitleRanges: [],
        checkboxRanges: [],
        mergeRanges: [],
        dropdownCells: []
      },
    }

    const settingsConfig =  {
      list: props.list,
      spreadsheetRenderer: this.spreadsheetRenderer,
      mergeRanges: props.formattingRanges.mergeRanges,
      editMode: props.editMode,
      filters: props.filters
    }
    this.hotSettings = getHotSettings(settingsConfig)

    // reference to handsontable component
    this.hotTableComponent = React.createRef()
    // reference to handsontable instance
    this.hotInstance
  }

  componentDidMount() {
    // set state from props whenever component is mounted
    this.setState({formattingRanges: Object.assign({}, this.props.formattingRanges)})

    // add callbacks for events
    this.addHandsontableHooks()
  }

  shouldComponentUpdate(nextProps, nextState) {
    // prevent unnecessary updates on handsontable, since handsontable internally takes care of re-rendering when data changes
    return (!_.isEqual(this.props.editMode, nextProps.editMode)
      || (!_.isEqual(this.props.formattingRanges, nextProps.formattingRanges))
      || (!_.isEqual(this.props.selectedRevisionNumber, nextProps.selectedRevisionNumber))
      || (!_.isEqual(this.props.isLoading, nextProps.isLoading)) 
      || (!_.isEqual(this.state.formattingRanges, nextState.formattingRanges)) 
    )
  } 

  componentDidUpdate(prevProps) {
    const {
      list,
      formattingRanges,
      editMode,
      isLoading
    } = this.props

    if (prevProps.isLoading && !isLoading) this.addHandsontableHooks()

    this.setState({
      formattingRanges: Object.assign({}, formattingRanges),
    }, () => {
      this.updateHotSettings(list, this.spreadsheetRenderer, formattingRanges.mergeRanges, editMode)
    })
  }

  addHandsontableHooks = () => {
    // The Handsontable instance is stored under the `hotInstance` property of the wrapper component.
    this.hotInstance = this.hotTableComponent.current.hotInstance
    this.hotInstance.addHook('beforeCreateRow', (index, amount) => this.onModifyRow(index, amount));
    this.hotInstance.addHook('beforeRemoveRow', (index, amount) => this.onModifyRow(index, -1 * amount));
  }

  updateHotSettings = (list, renderer, mergeRanges, editMode) => {
    !this.hotInstance.isDestroyed && this.hotInstance.updateSettings(getHotSettings({
      list: list,
      spreadsheetRenderer: renderer,
      mergeRanges: mergeRanges,
      editMode: editMode
    }))
  } 

  onModifyRow = (index, amount) => {
    let formattingRanges = Object.assign({}, this.state.formattingRanges)
    Object.keys(formattingRanges).forEach(key => {
      let ranges = formattingRanges[key].map(range => {
        if (range.hasOwnProperty('row') && range.row >= index) {
          range.row += amount
        } else if (range.hasOwnProperty('rowStart') && range.rowStart >= index) {
          range.rowStart += amount
          range.rowEnd += amount
        }
        return range
      })
      formattingRanges[key] = ranges
    })
    this.setState({formattingRanges: formattingRanges})
  }

  cellInRanges = (row, col, ranges) => {
    let inRange = false
    ranges.forEach(range => {
      if (row >= range.rowStart && row <= range.rowEnd && col >= range.colStart && col <= range.colEnd) inRange = true
    })
    return inRange
  }

  isDropdownCell = (row, col, dropdownCells) => {
    let cell = {}
    dropdownCells.forEach(dropdownCell => {
      if (row == dropdownCell.row && col == dropdownCell.col) cell = dropdownCell
    })
    return cell
  }

  spreadsheetRenderer = (row, col) => {
    const { formattingRanges } = this.state

    let cellProperties = {}

    if (this.cellInRanges(row, col, formattingRanges.titleRanges)) {
      cellProperties.renderer = this.titleRenderer 
      cellProperties.readOnly = true
    } else if (this.cellInRanges(row, col, formattingRanges.horizontalSubtitleRanges)) {
      cellProperties.renderer = this.horizontalsubtitleRenderer 
      cellProperties.readOnly = true
    } else if (this.cellInRanges(row, col, formattingRanges.verticalSubtitleRanges)) {
      cellProperties.renderer = this.verticalSubtitleRenderer 
      cellProperties.readOnly = true
    } else if (this.cellInRanges(row, col, formattingRanges.checkboxRanges)) {
      cellProperties.renderer = 'checkbox' 
    } else {
      const dropdownCell = this.isDropdownCell(row, col, formattingRanges.dropdownCells)
      if (!_.isEmpty(dropdownCell)) {
        cellProperties.type = 'dropdown'
        cellProperties.source = dropdownCell.source
      }
    }
    
    return cellProperties
  }

  titleRenderer(instance, td, row, col, prop, value, cellProperties) {
    Handsontable.renderers.TextRenderer.apply(this, arguments)
    td.style.fontWeight = 'bold'
    td.style.height = '35px'
    td.style.background = '#ffd966'
  }

  horizontalsubtitleRenderer(instance, td, row, col, prop, value, cellProperties) {
    Handsontable.renderers.TextRenderer.apply(this, arguments)
    td.style.fontWeight = 'bold'
    td.style.height = '28px'
    td.style.background = '#ddeebf'
  }

  verticalSubtitleRenderer(instance, td, row, col, prop, value, cellProperties) {
    Handsontable.renderers.TextRenderer.apply(this, arguments)
    td.style.fontWeight = 'bold'
  }

  render() {
    return (
      <div class={styles.specsTable}>
        {this.props.isLoading ? (
          <div class={styles.loader}>
            <CardLoading />
          </div> 
        ): (
          <HotTable
          ref={this.hotTableComponent}
          id="hot2"
          settings={this.hotSettings}
        /> 
        )}
      </div>
    )
  }
}


export default Spreadsheet